import type { FC, ReactNode } from "react";

export type HudPanelTone = "neutral" | "success" | "danger";

interface HudPanelProps {
  /** Section title — rendered with literal `[ LABEL ]` brackets per dialect. */
  title?: string;
  /** Subtitle below the title — dim mono uppercase. */
  subtitle?: string;
  /** Right-side adornment (status pill, action buttons, count chip). */
  actions?: ReactNode;
  /** Add a faint pixel-grid background. Off by default — opt in for hero panels. */
  pixelGrid?: boolean;
  /** Accent tone for corner brackets and bracket glyphs in the title. */
  tone?: HudPanelTone;
  className?: string;
  bodyClassName?: string;
  children: ReactNode;
}

const TONE_ACCENT: Record<HudPanelTone, string> = {
  danger: "text-rose-500/70 dark:text-rose-400/70",
  neutral: "text-slate-400 dark:text-slate-600",
  success: "text-emerald-500/80 dark:text-emerald-400/80",
};

const CornerBracket: FC<{
  position: "tl" | "tr" | "bl" | "br";
  tone: HudPanelTone;
}> = ({ position, tone }) => {
  const map: Record<typeof position, string> = {
    bl: "bottom-1 left-1 border-b border-l",
    br: "bottom-1 right-1 border-b border-r",
    tl: "top-1 left-1 border-l border-t",
    tr: "top-1 right-1 border-r border-t",
  };
  return (
    <span
      aria-hidden
      className={`pointer-events-none absolute z-10 h-2 w-2 ${map[position]} ${TONE_ACCENT[tone]} border-current`}
    />
  );
};

/** Section panel in the HUD-CRT dialect.
 *
 * Provides the canonical chrome — corner brackets, dark-mode scanline overlay,
 * optional pixel-grid background, bracket-titled header — used to wrap data
 * tables, charts, lists, modal bodies. Mirrors the BurnRateModal silhouette
 * but tuned subtler for inline page panels (slate accent, not emerald).
 *
 * Use `tone="success"` for live/healthy panels, `tone="danger"` for incident
 * banners. Default `tone="neutral"` for general data panels.
 */
export const HudPanel: FC<HudPanelProps> = ({
  title,
  subtitle,
  actions,
  pixelGrid = false,
  tone = "neutral",
  className = "",
  bodyClassName = "",
  children,
}) => {
  const hasHeader = Boolean(title || subtitle || actions);
  return (
    <section
      className={`relative overflow-hidden border border-slate-300 bg-white dark:border-slate-700 dark:bg-slate-950/60 ${className}`}
      style={
        pixelGrid
          ? {
              backgroundImage:
                "linear-gradient(rgba(148,163,184,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(148,163,184,0.04) 1px, transparent 1px)",
              backgroundSize: "24px 24px",
            }
          : undefined
      }
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 hidden opacity-[0.04] dark:block"
        style={{
          backgroundImage:
            "repeating-linear-gradient(0deg, rgba(255,255,255,1) 0 1px, transparent 1px 3px)",
        }}
      />
      <CornerBracket position="tl" tone={tone} />
      <CornerBracket position="tr" tone={tone} />
      <CornerBracket position="bl" tone={tone} />
      <CornerBracket position="br" tone={tone} />

      {hasHeader && (
        <div className="relative flex flex-col gap-3 border-b border-slate-200 px-4 py-3 dark:border-slate-800 md:flex-row md:items-center md:justify-between">
          <div className="min-w-0">
            {title && (
              <h2 className="flex items-center gap-1.5 font-mono text-[11px] font-bold uppercase tracking-[0.22em] text-slate-700 dark:text-slate-200">
                <span aria-hidden className={TONE_ACCENT[tone]}>
                  [
                </span>
                <span>{title}</span>
                <span aria-hidden className={TONE_ACCENT[tone]}>
                  ]
                </span>
              </h2>
            )}
            {subtitle && (
              <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.16em] text-slate-500 dark:text-slate-500">
                {subtitle}
              </p>
            )}
          </div>
          {actions && (
            <div className="flex flex-wrap items-center gap-2">{actions}</div>
          )}
        </div>
      )}

      <div className={`relative ${bodyClassName}`}>{children}</div>
    </section>
  );
};
